import React from "react";
import { Pagination as BootstrapPagination } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import { PaginationBarProps } from '@features/invitations/types/paginationBarProps';

const MAX_VISIBLE_PAGES = 5;

const getVisiblePages = (currentPage: number, totalPages: number): Array<number | string> => {
    const pages: Array<number | string> = [];
    const halfVisible = Math.floor(MAX_VISIBLE_PAGES / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);

    if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
        if (startPage === 1) {
            endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);
        } else {
            startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
        }
    }

    if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
            pages.push("...");
        }
    }

    for (let i = startPage; i <= endPage; i += 1) {
        pages.push(i);
    }

    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            pages.push("...");
        }
        pages.push(totalPages);
    }

    return pages;
};

const PaginationBar: React.FC<PaginationBarProps> = ({ page, totalPages, onPage }) => {
    if (totalPages <= 1) {
        return null;
    }

    const safePage = Math.min(Math.max(page, 1), totalPages);
    const visiblePages = getVisiblePages(safePage, totalPages);

    return (
        <BootstrapPagination className="mb-0 ntcog-pagination-bar" data-testid="pagination-bar">
            <BootstrapPagination.First
                disabled={safePage === 1}
                onClick={() => onPage(1)}
                data-testid="pagination-first"
            >
                <FiChevronsLeft />
            </BootstrapPagination.First>
            <BootstrapPagination.Prev
                disabled={safePage === 1}
                onClick={() => onPage(safePage - 1)}
                data-testid="pagination-prev"
            >
                <FiChevronLeft />
            </BootstrapPagination.Prev>
            {visiblePages.map((pageItem, index) => {
                const isCurrentPage = pageItem === safePage;
                const isEllipsis = pageItem === "...";

                return (
                    <BootstrapPagination.Item
                        key={`${pageItem}-${index}`}
                        active={isCurrentPage}
                        disabled={isEllipsis}
                        onClick={() => typeof pageItem === "number" && onPage(pageItem)}
                        data-testid={
                            isCurrentPage
                                ? "pagination-current"
                                : isEllipsis
                                    ? `pagination-ellipsis-${index}`
                                    : `pagination-page-${pageItem}`
                        }
                    >
                        {pageItem}
                    </BootstrapPagination.Item>
                );
            })}
            <BootstrapPagination.Next
                disabled={safePage === totalPages}
                onClick={() => onPage(safePage + 1)}
                data-testid="pagination-next"
            >
                <FiChevronRight />
            </BootstrapPagination.Next>
            <BootstrapPagination.Last
                disabled={safePage === totalPages}
                onClick={() => onPage(totalPages)}
                data-testid="pagination-last"
            >
                <FiChevronsRight />
            </BootstrapPagination.Last>
        </BootstrapPagination>
    );
};

export default PaginationBar;